import { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import { commandCenterApi } from '../../utils/api';
import { toast } from '../../components/Toast';
import Icon from '../../components/Icon';
import SettingsToggle from '../../components/SettingsToggle';
import { DOKKA_DOC_TYPES } from './modals';

// The customer's settings, edited INLINE on the customer card — the same three
// sections, headings, controls and copy as the app's /settings screen, because
// it is literally the same record. A modal would have made one row of data look
// like two different features depending on where you opened it.
//
// It expands in place under the read-only summary rather than covering the
// card, so the users and suppliers you were looking at stay on screen.

const SECTION_HEAD = {
  fontSize: '0.88rem', fontWeight: 700, color: '#888',
  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 16,
};
const FIELD_LABEL = { fontWeight: 600, fontSize: '0.95rem', color: '#1a1a2e' };
const FIELD_HELP  = { fontSize: '0.82rem', color: '#888', marginTop: 3 };
const RULE        = { borderTop: '1px solid #f0f0f0', margin: '24px 0' };

export default function CustomerSettingsForm({ customer, onSaved, onCancel, closeRef }) {
  // The version this form is editing FROM. State, not a prop derivative: a 409
  // hands back the row as it now stands, and adopting it has to move the
  // baseline too. Leave the baseline pinned to the originally-loaded prop and
  // every retry re-sends the stale token and 409s again — a banner telling you
  // to refresh, in a form with nothing to refresh.
  const [baseline, setBaseline] = useState(() => customer.settings || {});
  const [vatRate, setVatRate] = useState(baseline.vat_rate ?? 18);
  const [tracking, setTracking] = useState(baseline.payment_tracking_enabled === 1);
  const [budgetModule, setBudgetModule] = useState(baseline.budget_module_enabled === 1);
  const [dokkaEmail, setDokkaEmail] = useState(baseline.dokka_email || '');
  const [types, setTypes] = useState(Array.isArray(baseline.dokka_forward_types) ? baseline.dokka_forward_types : []);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [guard, setGuard] = useState(false);

  const toggleType = (t) =>
    setTypes(cur => (cur.includes(t) ? cur.filter(x => x !== t) : [...cur, t]));

  /**
   * Adopt the row as it now stands on the server, after someone else saved.
   *
   * Fields AND baseline move together — moving only the fields would leave the
   * old token attached to new values, so the next save would 409 against edits
   * that already match the server. The form lands clean, on the newer version.
   */
  const applyCurrent = (current) => {
    setBaseline(current);
    setVatRate(current.vat_rate ?? 18);
    setTracking(current.payment_tracking_enabled === 1);
    setBudgetModule(current.budget_module_enabled === 1);
    setDokkaEmail(current.dokka_email || '');
    setTypes(Array.isArray(current.dokka_forward_types) ? current.dokka_forward_types : []);
    setConflict(null);
    toast('נטענה הגרסה העדכנית');
  };

  // Have the values actually moved? Comparing against what was loaded means a
  // change-and-change-back counts as clean, which is what a person expects —
  // "did I edit anything" is about the end state, not about keystrokes.
  // Doc types are compared as a SET: reordering the same selections is not an
  // edit, and checkbox order is an artifact of clicking, not intent.
  const dirty = useMemo(() => {
    const initialTypes = [...(Array.isArray(baseline.dokka_forward_types) ? baseline.dokka_forward_types : [])].sort();
    const nowTypes = [...types].sort();
    return (
      Number(vatRate) !== Number(baseline.vat_rate ?? 18)
      || tracking !== (baseline.payment_tracking_enabled === 1)
      || budgetModule !== (baseline.budget_module_enabled === 1)
      || dokkaEmail.trim() !== (baseline.dokka_email || '')
      || initialTypes.length !== nowTypes.length
      || initialTypes.some((t, i) => t !== nowTypes[i])
    );
  }, [vatRate, tracking, budgetModule, dokkaEmail, types, baseline]);

  // Closing must ask before discarding. The card header's own button routes
  // through here too (via closeRef) — otherwise there would be one exit that
  // guards your work and another, two centimetres away, that throws it away.
  const requestClose = useCallback(() => {
    if (dirty) setGuard(true);
    else onCancel();
  }, [dirty, onCancel]);

  useEffect(() => {
    if (closeRef) closeRef.current = requestClose;
    return () => { if (closeRef) closeRef.current = null; };
  }, [closeRef, requestClose]);

  // Also catch a tab close / reload with unsaved edits. The browser shows its
  // own generic wording; the only thing we control is whether it asks at all.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setConflict(null);
    try {
      await commandCenterApi.updateCustomerSettings(customer.id, {
        vat_rate: vatRate,
        payment_tracking_enabled: tracking,
        budget_module_enabled: budgetModule,
        dokka_email: dokkaEmail.trim(),
        dokka_forward_types: types,
        // Echo the version we loaded. Both this screen and the app's /settings
        // write the same row, so a concurrent save is rejected rather than
        // silently erasing whoever got there first.
        expected_updated_at: baseline.updated_at,
      });
      toast('ההגדרות נשמרו');
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.error || 'שגיאה';
      toast(msg, 'error');
      // Stay open on a conflict so the typed values survive while they look,
      // and keep the newer row the server sent back — it is what the banner's
      // button loads.
      if (err.response?.status === 409) {
        setConflict({ message: msg, current: err.response.data?.current || null });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    {/* Three ways out, deliberately. A plain confirm forces "discard or stay",
        which makes the destructive answer the fast one; the common intent when
        you hit close with edits pending is "oh — save it". */}
    {guard && (
      <Modal
        onClose={() => setGuard(false)}
        size={400}
        ariaLabel="יש שינויים שלא נשמרו"
      >
        <h2 className="modal-title">יש שינויים שלא נשמרו</h2>
        <p style={{ fontSize: '0.9rem', color: '#555', margin: '0 0 18px' }}>
          שינית את ההגדרות של {customer.name} ועדיין לא שמרת. מה לעשות?
        </p>
        <div className="modal-actions">
          {/* Reading order (right to left): the way OUT of the dialog first,
              then the destructive escape, then the action we want — which sits
              at the trailing edge like every other confirming button. */}
          <button type="button" className="btn btn-secondary" onClick={() => setGuard(false)}>
            המשך עריכה
          </button>
          <button type="button" className="btn btn-danger"
            onClick={() => { setGuard(false); onCancel(); }}>
            סגור בלי לשמור
          </button>
          <button type="button" className="btn btn-success" disabled={saving}
            onClick={() => { setGuard(false); handleSave({ preventDefault() {} }); }}>
            {saving ? 'שומר...' : <><Icon name="save" size={15} /> שמור וסגור</>}
          </button>
        </div>
      </Modal>
    )}

    <form onSubmit={handleSave} style={{ marginTop: 4 }}>
      {/* A conflict is the one error where "try again" is the wrong advice:
          the same stale token would be rejected the same way. The way out is
          to take the newer row and re-apply your change on top of it, so that
          is the button. It replaces what is on screen, and says so. */}
      {conflict && (
        <div className="cc-error" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
          <Icon name="alert" size={17} />
          <span>{conflict.message}</span>
          {conflict.current && (
            <button type="button" className="btn btn-sm btn-secondary"
              onClick={() => { applyCurrent(conflict.current); }}>
              <Icon name="undo" size={13} /> טען את הגרסה העדכנית
            </button>
          )}
        </div>
      )}

      {/* Payments */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={SECTION_HEAD}>תשלומים</h3>
        <SettingsToggle
          checked={tracking}
          onChange={setTracking}
          label="תיעוד תשלומים"
          description="הצגת אפשרויות תשלום, היסטוריית תשלומים וסינון לפי סטטוס תשלום"
        />
      </div>

      <div style={RULE} />

      {/* הוצאות והכנסות — identical toggle + copy to the app's /settings; the
          two screens edit the same record and must mirror each other. */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={SECTION_HEAD}>הוצאות והכנסות</h3>
        <SettingsToggle
          checked={budgetModule}
          onChange={setBudgetModule}
          label="מודול הוצאות והכנסות"
          description="קטגוריות, יעדי תקציב והזנת הכנסות — כולל שני הווידג'טים בעמוד הניתוח ועמוד הניהול"
        />
      </div>

      <div style={RULE} />

      {/* Accounting */}
      <div>
        <h3 style={SECTION_HEAD}>חשבונאות</h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={FIELD_LABEL}>שיעור מע"מ</div>
            <div style={FIELD_HELP}>ישמש בחישוב מע"מ אוטומטי בעת עריכת מסמך</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <input
              type="number" min="0" max="100" step="0.1"
              value={vatRate}
              onChange={e => setVatRate(parseFloat(e.target.value) || 0)}
              style={{
                width: 80, padding: '6px 10px', border: '1.5px solid #dde3ec',
                borderRadius: 8, fontSize: '1rem', fontWeight: 600, textAlign: 'center',
                direction: 'ltr',
              }}
            />
            <span style={{ fontWeight: 600, color: '#555' }}>%</span>
          </div>
        </div>
      </div>

      <div style={RULE} />

      {/* Dokka */}
      <div>
        <h3 style={SECTION_HEAD}>העברה להנהלת חשבונות (Dokka)</h3>

        <div style={{ marginBottom: 20 }}>
          <div style={{ ...FIELD_LABEL, marginBottom: 6 }}>כתובת מייל ב-Dokka</div>
          <div style={{ ...FIELD_HELP, marginTop: 0, marginBottom: 10 }}>
            כתובת המייל הייחודית של המסעדה במערכת Dokka — אליה יישלחו המסמכים המאושרים.
          </div>
          <input
            type="email"
            value={dokkaEmail}
            onChange={e => setDokkaEmail(e.target.value)}
            placeholder="restaurant-name@dokka.com"
            style={{
              width: '100%', maxWidth: 380, padding: '8px 12px',
              border: '1.5px solid #dde3ec', borderRadius: 8,
              fontSize: '0.95rem', direction: 'ltr',
            }}
          />
        </div>

        <div>
          <div style={{ ...FIELD_LABEL, marginBottom: 6 }}>סוגי מסמכים שיועברו אוטומטית בעת אישור</div>
          <div style={{ ...FIELD_HELP, marginTop: 0, marginBottom: 12 }}>
            מסמך מסוג שמסומן כאן יישלח אוטומטית ל-Dokka ברגע שיאושר. שאר הסוגים אפשר לשלוח ידנית מתוך עמוד המסמך.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
            {DOKKA_DOC_TYPES.map(t => (
              <label key={t} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 10px', border: '1.5px solid #dde3ec',
                borderRadius: 8, cursor: 'pointer',
                background: types.includes(t) ? '#e8f0fe' : '#fff',
                fontSize: '0.9rem',
              }}>
                <input type="checkbox" checked={types.includes(t)}
                  onChange={() => toggleType(t)} style={{ cursor: 'pointer' }} />
                <span>{t}</span>
              </label>
            ))}
          </div>
          {!dokkaEmail.trim() && types.length > 0 && (
            <div style={{ color: '#92610a', fontSize: '0.82rem', marginTop: 10 }}>
              בלי כתובת דוקה שום מסמך לא יועבר בפועל.
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <button type="submit" className="btn btn-success" disabled={saving}>
          {saving ? 'שומר...' : <><Icon name="save" size={16} /> שמור הגדרות</>}
        </button>
        <button type="button" className="btn btn-secondary" onClick={requestClose}>ביטול</button>
      </div>
    </form>
    </>
  );
}
